import { GlobalStoreCtx } from '@/stores/GlobalStore';
import { copyToClipboard } from '@/utils';
import { getYaNative } from '@yanolja-org/ya-native';
import Head from 'next/head';
import qs from 'qs';
import React, { useContext, useEffect, useState } from 'react';

import BottomSheet from './DesignSystem/BottomSheet/BottomSheet';
import BottomSheetContentsSelectIcon, {
  SelectIconSet,
} from './DesignSystem/BottomSheet/Contents/BottomSheetContentsSelectIcon';
import { getSVG } from './styles/mixins';
import { shareKaKaoMessage } from '@/utils/KakaoShareUtils';
import { EN_APP_DEVICE } from '@/utils/functions/device';

export type ShareChannel = 'kakaotalk' | 'linkcopy' | 'facebook' | 'sms';

interface IShareProps {
  isOpen: boolean;
  kakaoSettings?: Kakao.Link.DefaultFeedSettings;
  url?: string;
  deeplink?: string;
  hideFacebook?: boolean;
  smsMessage?: string;
  onRequestClose: () => void;
  onShare?: (channel: ShareChannel) => void;
}

const Share: React.FC<IShareProps> = ({
  isOpen,
  kakaoSettings,
  url,
  deeplink,
  hideFacebook,
  smsMessage,
  onRequestClose,
  onShare,
}) => {
  const globalStore = useContext(GlobalStoreCtx);
  const [isKakaoLoaded, setKakaoLoaded] = useState(false);

  useEffect(() => {
    if (window && typeof Kakao !== 'undefined' && !Kakao?.isInitialized()) {
      Kakao?.init(window.ya__.kakaoAPIKey);
      setKakaoLoaded(true);
    }
  }, []);

  const handleKakaoShare = (): void => {
    if (kakaoSettings) {
      shareKaKaoMessage({ kakaoSettings, url, deeplink });

      onShare?.('kakaotalk');
      onRequestClose();
    }
  };

  const handleFacebookShare = (): void => {
    if (url) {
      const yaNative = getYaNative();

      if (yaNative.isEnabled) {
        yaNative.shareContent({
          sendTo: 'facebook',
          data: {
            url,
          },
        });
      } else {
        const queryString = qs.stringify({ u: url });
        const openUrl = `https://www.facebook.com/sharer.php?${queryString}`;
        window.open(openUrl, 'popup');
      }

      onShare?.('facebook');
      onRequestClose();
    }
  };

  const handleLinkCopy = async (): Promise<void> => {
    if (url) {
      try {
        const yaNative = getYaNative();

        if (yaNative.isEnabled) {
          yaNative.shareContent({
            sendTo: 'clipboard',
            data: {
              content: url,
            },
          });
        } else {
          await copyToClipboard(url);
        }

        globalStore.toast.addItem({ key: 'address-copied', text: '주소를 클립보드에 복사했습니다', timeout: 2000 });

        onShare?.('linkcopy');
        onRequestClose();
      } catch (e) {
        console.error(e);
        globalStore.toast.addItem({
          key: 'address-copy-failed',
          text: '주소를 클립보드에 복사하지 못했습니다',
          timeout: 2000,
        });
      }
    }
  };

  const handleSmsShare = (): void => {
    // sms 줄바꿈 문자는 %0a
    // & 문자가 포함된경우 안드로이드는 두번 디코딩하게 되므로 두번 인코딩해야한다.
    const isIOS = globalStore.Device === EN_APP_DEVICE.IOS;
    const encodedUrl = isIOS ? encodeURIComponent(url || '') : encodeURIComponent(encodeURIComponent(url || ''));
    const body = `${smsMessage}%0a%0a${encodedUrl}`;
    const yaNative = getYaNative();
    if (yaNative?.isEnabled) {
      if (yaNative.enabledFor === 'ios') {
        window.location.href = `sms:&body=${body}`;
      } else {
        window.location.href = `sms:?body=${body}`;
      }
    } else if (isIOS) {
      window.location.href = `sms:&body=${body}`;
    } else {
      window.location.href = `sms:?body=${body}`;
    }
    onShare?.('sms');
    setTimeout(() => onRequestClose(), 100);
  };

  return (
    <>
      <Head>
        <script key="kakao-sdk" type="text/javascript" src="//yaimg.yanolja.com/joy/sunny/static/images/kakao.min.js" />
      </Head>
      <BottomSheet isOpen={isOpen} onRequestClose={onRequestClose}>
        {{
          title: '공유하기',
          body: (
            <BottomSheetContentsSelectIcon>
              {
                [
                  isKakaoLoaded &&
                    kakaoSettings && [
                      <img key={0} src={getSVG('/static/images/bottomsheet-foundation-icon-kakaotalk.svg')} alt="" />,
                      '카카오톡',
                      handleKakaoShare,
                    ],
                  !!url &&
                    !hideFacebook && [
                      <img key={1} src={getSVG('/static/images/bottomsheet-foundation-icon-facebook.svg')} alt="" />,
                      '페이스북',
                      handleFacebookShare,
                    ],
                  url && [
                    <img key={2} src={getSVG('/static/images/bottomsheet-foundation-icon-copylink.svg')} alt="" />,
                    '링크복사',
                    handleLinkCopy,
                  ],
                  !globalStore.IsPc &&
                    smsMessage && [
                      <img key={3} src={getSVG('/static/images/bottomsheet-foundation-icon-sms.svg')} alt="" />,
                      'SMS',
                      handleSmsShare,
                    ],
                ].filter(Boolean) as SelectIconSet[]
              }
            </BottomSheetContentsSelectIcon>
          ),
        }}
      </BottomSheet>
    </>
  );
};

export default Share;
